import { useTranslation } from 'react-i18next';
import { useUrlPage } from '../../hooks/attendance/urlPage';
import { useSuspensePaginatedRedVelvetQuery } from '../../hooks/paginatedRedVelvetQuery';
import { Alert, Header, SpaceBetween } from '@amzn/awsui-components-react';
import { AccessibleSuspenseLoading } from '../accessibility/accessibleSuspenseLoading';
import { EventDetailsTable } from '../attendance/coordinator/coordinatorEventOwner';
import { useInjectEventDetailsForFirstPageOfQueryData } from '../../hooks/events';
import { DEFAULT_COORDINATOR_SHIFT_PAGE_SIZE } from './coordinatorEventShiftsTablePaginationPreference';
import { UrlControlledPagination } from '../../hooks/urlControlledPagination';
import { COORDINATOR_EVENT_SHIFTS_LIST, EVENT_CREATION } from '../../utils/constants';
import { featureIsEnabled, useFeatures } from '../../hooks/features';
import { useSearchParams } from 'react-router-dom';
import { EventStatus } from '@amzn/red-velvet-api';
import { NoActivitiesViewComponent } from './components/NoActivitiesViewComponent';
import { DEFAULT_EVENT_PAGE_SIZE } from './constants';

export type CoordinatorEventShiftsListProps = {
  startDate?: Date;
  endDate?: Date;
  sort: 'newest-first' | 'oldest-first';
  status?: EventStatus;
};

export function SuspenseCoordinatorEventShiftsList({
  startDate,
  endDate,
  sort,
  status,
}: CoordinatorEventShiftsListProps) {
  const { t } = useTranslation(['activity', 'translation']);
  const { page } = useUrlPage(1);
  const features = useFeatures();
  const [searchParams] = useSearchParams();
  const isEventCreationEnabled = featureIsEnabled(EVENT_CREATION, features, searchParams);

  const injectEventDetailsInReactCache = useInjectEventDetailsForFirstPageOfQueryData();
  // GOLDEN PATH NOTE
  // We are using the suspense query here for accessibility, since it will make the
  // accessible loading in the parent work correctly
  const { queryResult } = useSuspensePaginatedRedVelvetQuery(
    {
      redVelvetQueryKey: [
        'getEventDetailsManagedByUser',
        {
          descending: sort === 'newest-first',
          startDateTime: startDate ? `${startDate.toISOString()}` : undefined,
          endDateTime: endDate ? `${endDate.toISOString()}` : undefined,
          shiftPageSize: DEFAULT_COORDINATOR_SHIFT_PAGE_SIZE,
          pageSize: DEFAULT_EVENT_PAGE_SIZE,
          ...(isEventCreationEnabled ? { status: status } : {}),
        },
      ],
    },
    page
  );

  if (!queryResult.data) {
    return (
      <Alert
        type="error"
        i18nStrings={{
          errorIconAriaLabel: t('coordinatorEventShiftsList.errorAria'),
          dismissAriaLabel: t('alert.dismiss.error'),
        }}
      >
        {t('coordinatorEventShiftsList.beyondPageIndex', { page })}
      </Alert>
    );
  }

  if (queryResult.error) {
    return (
      <Alert
        type="error"
        i18nStrings={{
          errorIconAriaLabel: t('coordinatorEventShiftsList.errorAria'),
          dismissAriaLabel: t('alert.dismiss.error'),
        }}
      >{`${queryResult.error}`}</Alert>
    );
  }

  if (!queryResult.data?.eventDetails || queryResult.data?.eventDetails.length === 0) {
    return <NoActivitiesViewComponent analyticsPrefix={COORDINATOR_EVENT_SHIFTS_LIST} />;
  }

  const eventDetails = queryResult.data?.eventDetails ?? [];
  injectEventDetailsInReactCache(eventDetails);
  return (
    <div>
      <SpaceBetween direction="vertical" size="s">
        {eventDetails.map((eventDetail, index) => (
          <EventDetailsTable
            key={eventDetail.event.eventId}
            eventId={eventDetail.event.eventId}
            fromEventDetailPage={false}
          />
        ))}
      </SpaceBetween>
    </div>
  );
}
export function CoordinatorEventShiftsList({
  startDate,
  endDate,
  sort,
  status,
}: CoordinatorEventShiftsListProps) {
  const namespaces = ['activity', 'translation'];
  const { t } = useTranslation(namespaces);
  const features = useFeatures();
  const [searchParams] = useSearchParams();
  const getEventDetailsManagedByUserAPIInput = {
    pageSize: DEFAULT_EVENT_PAGE_SIZE,
    descending: sort === 'newest-first',
    startDateTime: startDate ? `${startDate.toISOString()}` : undefined,
    endDateTime: endDate ? `${endDate.toISOString()}` : undefined,
    shiftPageSize: DEFAULT_COORDINATOR_SHIFT_PAGE_SIZE,
    ...(featureIsEnabled(EVENT_CREATION, features, searchParams) ? { status: status } : {}),
  };
  const paginationProps = {
    paginationPrefix: 'coordinatorEventShiftsList.pagination',
    namespaces: namespaces,
    tableName: t('coordinatorEventShiftsList.tableName'),
  };
  return (
    <SpaceBetween direction="vertical" size="s">
      {/* GOLDEN PATH NOTE
            For accessibility reasons, we always want to show the pagination, even if there is no data.
            This allows users to rapidly paginate even while the content of another page is loading */}
      <Header
        actions={
          <UrlControlledPagination
            redVelvetQueryKey={[
              'getEventDetailsManagedByUser',
              getEventDetailsManagedByUserAPIInput,
            ]}
            {...paginationProps}
          />
        }
      />
      {/* GOLDEN PATH NOTE
                Since we are actually displaying loaded data here, we want to make sure it is accessible
                We need to use AccessibleSuspenseLoading RIGHT before the component that provides a suspense
                query to make sure the suspense contract is upheld.  We also name the child with the Suspense
                name to indicate the presence of the contract */}
      <AccessibleSuspenseLoading loadingText={t('coordinatorEventShiftsList.loading')}>
        <SuspenseCoordinatorEventShiftsList
          startDate={startDate}
          endDate={endDate}
          sort={sort}
          status={status}
        />
      </AccessibleSuspenseLoading>
      <Header
        actions={
          <UrlControlledPagination
            redVelvetQueryKey={[
              'getEventDetailsManagedByUser',
              getEventDetailsManagedByUserAPIInput,
            ]}
            {...paginationProps}
          />
        }
      />
    </SpaceBetween>
  );
}
